import { useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { toOptions } from '~/components/form';
import { RecordPageShell, type PageAction } from '~/components/layout';
import { Card, CardBody, CardFooter, CardSection, FormActions } from '~/components/ui';
import {
  AGREEMENT_STATUSES,
  AGREEMENT_TYPES,
  FEE_CYCLES,
  MLS_DATA_TYPES,
} from '~/components/Constants/mlsAgreementsConstants';
import { DocumentsPanel } from '~/components/pages/mls-agreements/DocumentsPanel';
import { HistoryPanel } from '~/components/pages/mls-agreements/HistoryPanel';
import { isNotFound } from '~/components/pages/mls-agreements/errors';
import {
  fieldId,
  RecordSections,
  SaveError,
  type FieldSpec,
  type SectionSpec,
} from '~/components/pages/mls-agreements/fields';
import type { MembershipRecord, Scalar } from '~/components/pages/mls-agreements/types';
import { routes } from '~/app/routes';
import { useFormValidation } from '~/hooks/useFormValidation';
import { useRecordDraft } from '~/hooks/useRecordDraft';
import { useRecordSave } from '~/hooks/useRecordSave';
import { useUnsavedGuard } from '~/hooks/useUnsavedGuard';
import { queryErrorMessage } from '~/modules/query';
import {
  useCreateAgreementMembershipMutation,
  useGetAgreementMembershipQuery,
  useUpdateAgreementMembershipMutation,
} from '~/modules/query/mlsAgreements';

type Draft = Record<
  | 'customerId'
  | 'mlsId'
  | 'product'
  | 'dataType'
  | 'assocFeeAmount'
  | 'assocFeeCycle'
  | 'assocFeeInvoicedBy'
  | 'vendorFeeAmount'
  | 'vendorFeeCycle'
  | 'vendorFeeInvoicedBy'
  | 'nextBillDate'
  | 'agentName'
  | 'agentEmail'
  | 'brokerageName'
  | 'brokerageEmail'
  | 'brokerageAddress'
  | 'brokerName'
  | 'brokerEmail'
  | 'agreementType'
  | 'agreementStatus'
  | 'agreementExpiration',
  Scalar
>;

const toDraft = (res: MembershipRecord): Draft => ({
  customerId: res.customerId ?? '',
  mlsId: res.mlsId ?? '',
  product: res.product ?? '',
  dataType: res.dataType ?? MLS_DATA_TYPES[0],
  assocFeeAmount: res.assocFeeAmount ?? '',
  assocFeeCycle: res.assocFeeCycle ?? FEE_CYCLES[0],
  assocFeeInvoicedBy: res.assocFeeInvoicedBy ?? '',
  vendorFeeAmount: res.vendorFeeAmount ?? '',
  vendorFeeCycle: res.vendorFeeCycle ?? FEE_CYCLES[0],
  vendorFeeInvoicedBy: res.vendorFeeInvoicedBy ?? '',
  nextBillDate: res.nextBillDate ?? '',
  agentName: res.agentName ?? '',
  agentEmail: res.agentEmail ?? '',
  brokerageName: res.brokerageName ?? '',
  brokerageEmail: res.brokerageEmail ?? '',
  brokerageAddress: res.brokerageAddress ?? '',
  brokerName: res.brokerName ?? '',
  brokerEmail: res.brokerEmail ?? '',
  agreementType: res.agreementType ?? AGREEMENT_TYPES[0],
  agreementStatus: res.agreementStatus ?? AGREEMENT_STATUSES[0],
  agreementExpiration: res.agreementExpiration ?? '',
});

const feeSection = (title: string, prefix: 'assoc' | 'vendor'): SectionSpec<Draft> => ({
  title,
  columns: 3,
  fields: [
    { key: `${prefix}FeeAmount`, label: 'Amount', kind: 'number' },
    { key: `${prefix}FeeCycle`, label: 'Cycle', kind: 'select', options: toOptions(FEE_CYCLES) },
    { key: `${prefix}FeeInvoicedBy`, label: 'Invoiced by' },
  ] as FieldSpec<Draft>[],
});

/** `fromCustomer` / `fromMls`: the link that opened a new membership already names that side. */
const sections = (
  isNew: boolean,
  fromCustomer: boolean,
  fromMls: boolean,
): SectionSpec<Draft>[] => [
  {
    title: 'Agreement',
    fields: [
      {
        key: 'customerId',
        label: 'Customer ID',
        kind: 'number',
        required: true,
        locked: fromCustomer,
      },
      { key: 'mlsId', label: 'MLS ID', kind: 'number', required: true, locked: fromMls },
      { key: 'product', label: 'Product', required: isNew },
      { key: 'dataType', label: 'Data type', kind: 'select', options: toOptions(MLS_DATA_TYPES) },
      {
        key: 'agreementType',
        label: 'Agreement type',
        kind: 'select',
        options: toOptions(AGREEMENT_TYPES),
      },
      {
        key: 'agreementStatus',
        label: 'Agreement status',
        kind: 'select',
        options: toOptions(AGREEMENT_STATUSES),
      },
      { key: 'agreementExpiration', label: 'Agreement expiration', kind: 'date' },
      { key: 'nextBillDate', label: 'Next bill date', kind: 'date' },
    ],
  },
  feeSection('Association fee', 'assoc'),
  feeSection('Vendor fee', 'vendor'),
  {
    title: 'Contacts',
    fields: [
      { key: 'agentName', label: 'Agent name' },
      { key: 'agentEmail', label: 'Agent email' },
      { key: 'brokerageName', label: 'Brokerage name' },
      { key: 'brokerageEmail', label: 'Brokerage email' },
      { key: 'brokerageAddress', label: 'Brokerage address', wide: true },
      { key: 'brokerName', label: 'Broker name' },
      { key: 'brokerEmail', label: 'Broker email' },
    ],
  },
];

const requiredId = (message: string) =>
  z.union([z.string(), z.number()]).refine((value) => String(value).trim() !== '', message);
const ids = {
  customerId: requiredId('Enter the customer ID.'),
  mlsId: requiredId('Enter the MLS ID.'),
};
// The service accepts a blank product, so only a new membership has to name one.
const CREATE_SCHEMA = z.object({ ...ids, product: z.string().trim().min(1, 'Enter the product.') });
const EDIT_SCHEMA = z.object(ids);

const FORM_ID = 'membership-form';

/** Create (`/new`, optionally `?customerId=` / `?mlsId=`) and edit (`/:id`) one membership (DESIGN.md §1.6). */
export default function MembershipPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const membershipId = id ?? '';
  const isNew = !id;

  const fromCustomer = isNew && !!params.get('customerId');
  const fromMls = isNew && !!params.get('mlsId');
  const query = useGetAgreementMembershipQuery(membershipId, { skip: isNew });
  const record = query.currentData as MembershipRecord | undefined;
  const { draft, patch, dirty, markSaved } = useRecordDraft<MembershipRecord, Draft>(
    isNew
      ? {
          customerId: params.get('customerId') ?? undefined,
          mlsId: params.get('mlsId') ?? undefined,
        }
      : record,
    { id: id ?? 'new', version: record?.updatedAt, toDraft },
  );
  const guard = useUnsavedGuard(dirty);
  const v = useFormValidation(isNew ? CREATE_SCHEMA : EDIT_SCHEMA, draft, (field) =>
    fieldId('membership', field),
  );

  const [createMembership] = useCreateAgreementMembershipMutation();
  const [updateMembership] = useUpdateAgreementMembershipMutation();
  const createdId = useRef<string | undefined>(undefined);

  const save = useRecordSave<Draft>({
    save: async (d) => {
      if (isNew) createdId.current = (await createMembership(d).unwrap()).id;
      else
        await updateMembership({
          id: membershipId,
          data: { ...d, updatedAt: record?.updatedAt },
        }).unwrap();
    },
    success: isNew ? 'Membership created' : 'Membership saved',
    failure: 'The membership could not be saved. Try again.',
  });

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft || !v.submit()) return;
    if (!(await save.submit(draft))) return;
    if (!isNew) return markSaved(draft);
    guard.release();
    navigate(
      createdId.current ? routes.mlsAgreementsMembership(createdId.current) : routes.mlsAgreements,
      { replace: true },
    );
  };

  const title = isNew ? 'Create membership' : 'Membership';
  const notFound = !isNew && isNotFound(query.error);
  const failed = query.isError && !notFound;
  const actions: PageAction[] | undefined = record && [
    {
      label: 'View MLS',
      kind: 'secondary',
      href: routes.mlsAgreementsMls(String(record.mlsId)),
      testId: 'membership-view-mls',
    },
    {
      label: 'View customer',
      kind: 'secondary',
      href: routes.mlsAgreementsCustomer(String(record.customerId)),
      testId: 'membership-view-customer',
    },
  ];

  const specs = sections(isNew, fromCustomer, fromMls);

  return (
    <RecordPageShell
      title={title}
      breadcrumbs={[{ label: 'MLS agreements', href: routes.mlsAgreements }, { label: title }]}
      actions={actions}
      loading={!draft && !query.isError}
      notFound={notFound}
      notFoundTitle="Membership not found"
      backTo={routes.mlsAgreements}
      backLabel="Back to MLS agreements"
      error={failed ? { what: 'the membership', message: queryErrorMessage(query.error) } : null}
      onRetry={() => query.refetch()}
      guard={guard}
      testId="membership"
    >
      {draft && (
        <>
          <form id={FORM_ID} noValidate onSubmit={submit}>
            <Card>
              <CardBody className="space-y-6">
                <SaveError message={save.error} />
                <RecordSections
                  prefix="membership"
                  sections={specs}
                  draft={draft}
                  patch={patch}
                  errors={v.errors}
                  touch={v.touch}
                />
              </CardBody>
              <CardFooter>
                <FormActions
                  form={FORM_ID}
                  discardHref={routes.mlsAgreements}
                  {...(isNew
                    ? { saveLabel: 'Create membership', busyLabel: 'Creating…' }
                    : { disabled: !dirty, disabledReason: 'No changes to save' })}
                  busy={save.busy}
                  saveTestId="membership-save"
                />
              </CardFooter>
            </Card>
          </form>

          {!isNew && (
            <Card>
              <CardBody className="space-y-6">
                <CardSection title="Documents">
                  <DocumentsPanel
                    entityType="membership"
                    entityId={membershipId}
                    documents={record?.documents ?? []}
                  />
                </CardSection>

                <CardSection title="History">
                  <HistoryPanel entityType="membership" entityId={membershipId} />
                </CardSection>
              </CardBody>
            </Card>
          )}
        </>
      )}
    </RecordPageShell>
  );
}
